import React from "react";
import { Alert, Button, SpaceBetween } from "@amzn/awsui-components-react";
import { Stage } from "../../lusca-search-api/generated-src";
import { GroupsFilterQuery } from "./groupsFilter";
import {
  useInvestigationInbox,
  useInvestigationList,
  useUninvestigatedGroups,
} from "../../hooks/useInvestigations";
import useCurrentUser from "../../hooks/useCurrentUser";
import { InvestigationTable, summarise, toRows } from "./InvestigationTable";
import { InvestigationFunnel } from "./InvestigationFunnel";
import { BacklogPreview } from "./InvestigationBacklog";
import { InvestigationGrowing } from "./InvestigationGrowing";
import { DlqSizeChart } from "./DlqSizeChart";
import {
  InvestigationSuggestions,
  SuggestionStart,
} from "./InvestigationSuggestions";
import { useTakeOwnership } from "./useTakeOwnership";
import {
  InvestigationSectionName,
  NEEDS_OWNER_LABEL,
  RowSeed,
  attentionInbox,
  attentionItems,
} from "./investigationUtils";

/** The pages the overview links out to (mirrors the side navigation). */
export type InvestigationSection = InvestigationSectionName;

export const OVERVIEW_UNOWNED_LIMIT = 5;
export const OVERVIEW_SUGGESTIONS_LIMIT = 3;

interface InvestigationOverviewProps {
  stage: Stage;
  onOpenInvestigation: (investigationId: string) => void;
  onOpenSection: (
    section: InvestigationSection,
    filter?: GroupsFilterQuery
  ) => void;
  onStartFromGroup: (seed: RowSeed) => void;
  onStartFromSuggestion: (start: SuggestionStart) => void;
}

/**
 * The home page: what needs attention, in one screen, without scrolling for it. The funnel
 * strip on top, the whole DLQ's size over time, then capped feeds — ownerless cases, TrackStars' root-caused patterns, the
 * populations growing fastest, and the biggest backlog groups — each linking to its full page. The registry tables live on
 * their own pages (Active, Mine, Resolved) reached from the side navigation.
 */
export const InvestigationOverview: React.FC<InvestigationOverviewProps> = ({
  stage,
  onOpenInvestigation,
  onOpenSection,
  onStartFromGroup,
  onStartFromSuggestion,
}) => {
  const currentUser = useCurrentUser();
  const activeList = useInvestigationList(stage, { view: "active" });
  const resolvedList = useInvestigationList(stage, { view: "resolved" });
  const groups = useUninvestigatedGroups(stage, false);
  const { takeOwnership, claimingId, justClaimedIds, toasts } =
    useTakeOwnership(stage, () => activeList.reset());

  const ownerless = activeList.items.filter((inv) => !inv.owner);
  const activeTotals = summarise(toRows(activeList.items, currentUser));
  const resolvedTotals = summarise(toRows(resolvedList.items, currentUser));
  // The bell's feed (same query, no extra request), so the count clears when it is read.
  const inbox = attentionInbox(useInvestigationInbox()?.data?.items);
  const attention = attentionItems(activeList.items, currentUser, inbox).length;

  if (activeList.error) {
    return (
      <Alert type="error" header="Investigation service unavailable">
        {activeList.error.message}. The DLQ dashboard keeps working;
        investigation state is temporarily unavailable.
      </Alert>
    );
  }

  return (
    <SpaceBetween size="l">
      {toasts.element}
      <InvestigationFunnel
        stage={stage}
        onOpenSection={onOpenSection}
        backlog={{
          loading: groups.isLoading,
          error: groups.error?.message,
          available: groups.data?.available,
          items: groups.data?.totalUnassigned ?? 0,
          groups: groups.data?.totalGroups ?? groups.data?.groups?.length ?? 0,
          impactUsd: groups.data?.totalUnassignedImpactUsd,
          snapshotTimestamp: groups.data?.snapshotTimestamp,
        }}
        active={{
          loading: activeList.isLoading,
          cases: activeList.items.length,
          more: !!activeList.hasMore,
          items: activeTotals.items,
          impactUsd: activeTotals.impactUsd,
          attention,
          needsOwner: ownerless.length,
        }}
        resolved={{
          loading: resolvedList.isLoading,
          cases: resolvedList.items.length,
          more: !!resolvedList.hasMore,
          items: resolvedTotals.items,
          impactUsd: resolvedTotals.impactUsd,
        }}
      />
      <DlqSizeChart stage={stage} />
      {(ownerless.length > 0 || activeList.isLoading) && (
        <InvestigationTable
          stage={stage}
          compact
          headerVariant="h2"
          title={NEEDS_OWNER_LABEL}
          description="Active investigations nobody holds. Click Needs an owner to take one so the work has someone accountable."
          emptyText="Every active investigation has an owner."
          items={ownerless.slice(0, OVERVIEW_UNOWNED_LIMIT)}
          currentUser={currentUser}
          attention={inbox}
          highlightIds={justClaimedIds}
          loading={activeList.isLoading}
          hasMore={ownerless.length > OVERVIEW_UNOWNED_LIMIT}
          loadMoreLabel={`See all ${ownerless.length}`}
          onLoadMore={() => onOpenSection("unowned")}
          headerActions={
            ownerless.length > OVERVIEW_UNOWNED_LIMIT ? undefined : (
              <Button onClick={() => onOpenSection("unowned")}>
                Open page
              </Button>
            )
          }
          onOpen={onOpenInvestigation}
          compactColumns={[
            "title",
            "status",
            "owner",
            "createdBy",
            "items",
            "impact",
            "updated",
          ]}
          onTakeOwnership={takeOwnership}
          takingOwnershipId={claimingId}
        />
      )}
      <InvestigationSuggestions
        stage={stage}
        limit={OVERVIEW_SUGGESTIONS_LIMIT}
        onSeeAll={() => onOpenSection("suggestions")}
        onStart={onStartFromSuggestion}
      />
      <InvestigationGrowing
        stage={stage}
        investigations={activeList.items}
        investigationsLoading={activeList.isLoading}
        onOpenInvestigation={onOpenInvestigation}
        onStartFromGroup={onStartFromGroup}
      />
      <BacklogPreview
        stage={stage}
        onSeeAll={() => onOpenSection("backlog")}
        onStart={onStartFromGroup}
        onStartFromSuggestion={onStartFromSuggestion}
      />
    </SpaceBetween>
  );
};

export default InvestigationOverview;
